import { buttonVariants } from "@coucou/ui";
import { useState } from "react";
import { loadSession } from "../lib/session";
import { API_URL, SALON_ID } from "../lib/trpc";

/**
 * Téléversement d'une photo de réalisation.
 *
 * **Le redimensionnement se fait ICI, dans le navigateur.** Une photo de
 * téléphone pèse 4 à 8 Mo ; envoyée telle quelle, elle occuperait le disque du
 * serveur, ralentirait la galerie, et obligerait l'API à embarquer une
 * bibliothèque de traitement d'image native. Le même travail fait par le
 * navigateur coûte quelques dizaines de millisecondes et ne dépend de rien.
 *
 * Deux tailles partent : l'image affichable (1600 px au plus grand côté) et
 * une vignette (400 px) pour la grille. La couleur dominante est calculée au
 * passage — sans elle, la galerie « saute » au chargement, chaque case restant
 * blanche jusqu'à l'arrivée de sa photo.
 */

type Redimensionnee = {
  blob: Blob;
  width: number;
  height: number;
};

/** Redimensionne en conservant les proportions, sans jamais agrandir. */
async function redimensionner(fichier: File, cote: number): Promise<Redimensionnee> {
  const bitmap = await createImageBitmap(fichier);
  const ratio = Math.min(1, cote / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * ratio);
  const height = Math.round(bitmap.height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob>((resolve) =>
    // WebP : environ 30 % de moins que le JPEG a qualite egale, et compris par
    // tous les navigateurs que ce poste utilise.
    canvas.toBlob((b) => resolve(b!), "image/webp", 0.82),
  );

  return { blob, width, height };
}

/** Couleur moyenne de l'image, pour le fond de la case avant chargement. */
async function couleurDominante(fichier: File): Promise<string> {
  const bitmap = await createImageBitmap(fichier);
  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;
  const contexte = canvas.getContext("2d")!;
  // Reduire a UN pixel : le navigateur fait la moyenne lui-meme, et c'est
  // exactement ce qu'on cherche.
  contexte.drawImage(bitmap, 0, 0, 1, 1);
  bitmap.close();

  const [r, g, b] = contexte.getImageData(0, 0, 1, 1).data;
  return `#${[r, g, b].map((v) => v!.toString(16).padStart(2, "0")).join("")}`;
}

export function PhotoUpload({
  onUploaded,
  label,
}: {
  /** Libelle du bouton. « + Photo » convient a une galerie, pas a un en-tete. */
  label?: string;
  /**
   * `url` accompagne l'identifiant : l'appelant peut afficher un apercu
   * immediat sans re-interroger la mediatheque pour une image qu'il vient
   * d'envoyer.
   */
  onUploaded: (assetId: string, url?: string) => void;
}) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const envoyer = async (fichier: File) => {
    setEnCours(true);
    setErreur(null);

    try {
      const [grande, vignette, couleur] = await Promise.all([
        redimensionner(fichier, 1600),
        redimensionner(fichier, 400),
        couleurDominante(fichier),
      ]);

      /*
       * Le nom du fichier d'origine voyage avec l'image : le serveur le place
       * devant l'empreinte, et l'URL publique garde un nom qui decrit la photo.
       */
      const base = fichier.name.replace(/\.[^.]+$/, "");
      const formulaire = new FormData();
      formulaire.append("image", grande.blob, `${base}.webp`);
      formulaire.append("thumbnail", vignette.blob, `${base}-vignette.webp`);
      formulaire.append("salonId", SALON_ID);
      formulaire.append("width", String(grande.width));
      formulaire.append("height", String(grande.height));
      formulaire.append("dominantColor", couleur);
      formulaire.append("altText", fichier.name.replace(/\.[^.]+$/, ""));

      /*
       * Le jeton part en en-tete, comme pour tRPC.
       *
       * Cet envoi ne comptait que sur le cookie de session, et le poste du
       * salon n'en a jamais eu : il se connecte par `Bearer`. Tout
       * televersement repondait donc 401 alors que le reste de
       * l'administration fonctionnait — un ecart d'autant plus difficile a
       * lire que la panne etait totale et silencieuse.
       *
       * `credentials: "include"` reste pour la surface web, ou le cookie est
       * le transport ; le serveur accepte les deux.
       */
      const session = loadSession();
      const reponse = await fetch(`${API_URL}/media`, {
        method: "POST",
        body: formulaire,
        headers: session ? { authorization: `Bearer ${session.token}` } : undefined,
        credentials: "include",
      });

      if (!reponse.ok) {
        throw new Error(
          reponse.status === 401
            ? "Votre session a expiré. Reconnectez-vous et réessayez."
            : reponse.status === 415
              ? "Format non accepté. Utilisez une photo JPEG, PNG ou WebP."
              : `Le serveur a refusé l'envoi (${reponse.status}).`,
        );
      }

      const asset = (await reponse.json()) as { id: string; url?: string };
      onUploaded(asset.id, asset.url);
    } catch (error) {
      setErreur(error instanceof Error ? error.message : "Envoi impossible.");
    } finally {
      setEnCours(false);
    }
  };

  return (
    <div>
      <label className="inline-block">
        <input
          type="file"
          accept="image/*"
          className="sr-only"
          disabled={enCours}
          onChange={(event) => {
            const fichier = event.target.files?.[0];
            // On vide le champ : sans cela, choisir deux fois la meme photo ne
            // declenche aucun evenement et rien ne se passe.
            event.target.value = "";
            if (fichier) void envoyer(fichier);
          }}
        />
        {/* Un `span` et non un `Button` : un vrai bouton n'active pas le
            `label` qui l'entoure, et le champ de fichier ne s'ouvrirait
            jamais. On reprend donc seulement l'apparence. */}
        <span
          className={buttonVariants({ variant: "primary", size: "md" })}
          aria-busy={enCours || undefined}
        >
          {enCours ? "Envoi…" : (label ?? "+ Photo")}
        </span>
      </label>

      {erreur && (
        <p role="alert" className="mt-2 text-sm text-grenat-700">
          {erreur}
        </p>
      )}
    </div>
  );
}
